/**
 * iosDebug.js — instrumentation Phase 27H pour debug iPhone "écran gris".
 *
 * Active UNIQUEMENT sur iPhone/iPad/iPod (no pollution Android/Desktop).
 * Tous les logs sont préfixés ``[GIVELY][IOS][BOOT|NETWORK|ERROR|RENDER|
 * STORAGE|PERF|INTERACTION|HEARTBEAT]`` pour grep facile en console
 * Safari + POSTés vers ``/api/telemetry/frontend-log`` (le user n'a pas
 * Web Inspector iPhone facilement, on a besoin de voir cote serveur).
 *
 * À importer EN PREMIER dans ``index.js`` AVANT toute autre logique
 * pour capturer crashes au boot.
 */

// URL ABSOLUE de l'API (cf. src/index.js) : « /api » relatif = 404 sur www.
import { API_URL } from "../config/backend";
import { modeDiagnosticDemarrage } from "./bootDebug";

const _UA = (typeof navigator !== "undefined" && navigator.userAgent) || "?";
const isIOS = /iPhone|iPad|iPod/i.test(_UA);
const isSafari = /Safari/i.test(_UA) && !/Chrome|CriOS|FxiOS/i.test(_UA);

// Singleton pour éviter double install si module re-importé.
let _installed = false;

function _post(category, event, extra) {
  try {
    const body = JSON.stringify({
      event: `[GIVELY][IOS][${category}] ${event}`,
      build: "phase27h_ios_debug",
      user_agent: _UA.slice(0, 200),
      ios: isIOS,
      safari: isSafari,
      ts: Date.now(),
      ...(extra || {}),
    });
    if (typeof navigator !== "undefined" && navigator.sendBeacon) {
      const blob = new Blob([body], { type: "application/json" });
      navigator.sendBeacon(`${API_URL}/telemetry/frontend-log`, blob);
      return;
    }
    if (typeof XMLHttpRequest !== "undefined") {
      const xhr = new XMLHttpRequest();
      xhr.open("POST", `${API_URL}/telemetry/frontend-log`, true);
      xhr.setRequestHeader("Content-Type", "application/json");
      xhr.send(body);
    }
  } catch (_) { /* swallow */ }
}

// 01/10/2026 — un jalon SAIN ne part plus vers le serveur (console seulement),
// sauf en mode diagnostic (`?debug_boot=1`, cf. utils/bootDebug.js). Les
// problèmes (`_err`) partent toujours.
const _diagnostic = modeDiagnosticDemarrage();

function _log(category, event, extra) {
  // eslint-disable-next-line no-console
  console.log(`[GIVELY][IOS][${category}]`, event, extra || "");
  if (_diagnostic) _post(category, event, extra);
}

function _err(category, event, extra) {
  // eslint-disable-next-line no-console
  console.error(`[GIVELY][IOS][${category}]`, event, extra || "");
  _post(category, event, extra);
}

export function installIOSDebug() {
  if (_installed) return;
  _installed = true;

  // Toujours log le contexte boot, même si pas iOS — permet de
  // comparer Android vs iPhone côté Railway.
  _log("BOOT", "instrumentation_install", {
    userAgent: _UA,
    platform: (typeof navigator !== "undefined" && navigator.platform) || "?",
    is_ios: isIOS,
    is_safari: isSafari,
    screen: typeof window !== "undefined"
      ? `${window.innerWidth}x${window.innerHeight}` : "?",
    pixel_ratio: typeof window !== "undefined"
      ? window.devicePixelRatio : null,
    online: typeof navigator !== "undefined" ? navigator.onLine : null,
    cookie_enabled: typeof navigator !== "undefined"
      ? navigator.cookieEnabled : null,
  });

  // À partir d'ici, on n'instrumente le reste QUE si iOS pour ne
  // pas polluer les autres devices.
  if (!isIOS) return;

  // ─── A. Lifecycle DOM/load ─────────────────────────────────────
  if (typeof document !== "undefined") {
    document.addEventListener("DOMContentLoaded", () => {
      _log("BOOT", "DOMContentLoaded");
    });
  }
  if (typeof window !== "undefined") {
    window.addEventListener("load", () => {
      _log("BOOT", "window_load");
    });
    window.addEventListener("beforeunload", () => {
      _log("BOOT", "beforeunload");
    });
    window.addEventListener("pagehide", (ev) => {
      _log("BOOT", "pagehide", { persisted: ev?.persisted });
    });
    window.addEventListener("pageshow", (ev) => {
      _log("BOOT", "pageshow", { persisted: ev?.persisted });
    });
  }

  // ─── B. Catch global erreurs ────────────────────────────────────
  if (typeof window !== "undefined") {
    window.addEventListener("error", (ev) => {
      _err("ERROR", "js_error", {
        message: String(ev?.message || "").slice(0, 480),
        filename: String(ev?.filename || "").slice(0, 200),
        lineno: ev?.lineno,
        colno: ev?.colno,
        stack: String(ev?.error?.stack || "").slice(0, 1500),
      });
    });
    window.addEventListener("unhandledrejection", (ev) => {
      const reason = ev?.reason || {};
      _err("ERROR", "unhandled_rejection", {
        message: String(reason?.message || reason || "").slice(0, 480),
        stack: String(reason?.stack || "").slice(0, 1500),
      });
    });
  }

  // ─── C. JS engine sanity check ─────────────────────────────────
  try {
    if (typeof Promise === "undefined") {
      _err("ERROR", "promise_undefined");
    } else {
      _log("BOOT", "promise_available");
    }
    if (typeof fetch === "undefined") {
      _err("ERROR", "fetch_undefined");
    }
  } catch (e) {
    _err("ERROR", "js_engine_check_threw", { msg: String(e) });
  }

  // ─── D. Storage tests (Safari Private mode bloque tout) ────────
  try {
    if (typeof localStorage !== "undefined") {
      localStorage.setItem("__gively_test", "1");
      const v = localStorage.getItem("__gively_test");
      localStorage.removeItem("__gively_test");
      _log("STORAGE", "localStorage_ok", { roundtrip: v === "1" });
    } else {
      _err("STORAGE", "localStorage_undefined");
    }
  } catch (e) {
    _err("STORAGE", "localStorage_blocked", { msg: String(e).slice(0, 200) });
  }
  try {
    if (typeof sessionStorage !== "undefined") {
      sessionStorage.setItem("__gively_test", "1");
      sessionStorage.removeItem("__gively_test");
      _log("STORAGE", "sessionStorage_ok");
    }
  } catch (e) {
    _err("STORAGE", "sessionStorage_blocked", { msg: String(e).slice(0, 200) });
  }

  // ─── E. Performance / mémoire ──────────────────────────────────
  try {
    const mem = (typeof performance !== "undefined" && performance.memory) || null;
    if (mem) {
      _log("PERF", "memory", {
        used_mb: Math.round(mem.usedJSHeapSize / 1024 / 1024),
        total_mb: Math.round(mem.totalJSHeapSize / 1024 / 1024),
        limit_mb: Math.round(mem.jsHeapSizeLimit / 1024 / 1024),
      });
    } else {
      _log("PERF", "memory_api_unavailable");
    }
  } catch (e) { /* ignore */ }

  // ─── F. Network instrumentation (window.fetch) ──────────────────
  if (typeof window !== "undefined" && window.fetch) {
    const _origFetch = window.fetch.bind(window);
    window.fetch = async (input, init) => {
      const url = typeof input === "string" ? input : input?.url || "?";
      const t0 = Date.now();
      try {
        const resp = await _origFetch(input, init);
        const dt = Date.now() - t0;
        // Log uniquement si erreur OU lenteur (pour ne pas spammer).
        if (!resp.ok || dt > 3000) {
          _log("NETWORK", "fetch_response", {
            url: String(url).slice(0, 200),
            status: resp.status,
            ok: resp.ok,
            duration_ms: dt,
          });
        }
        return resp;
      } catch (e) {
        _err("NETWORK", "fetch_failed", {
          url: String(url).slice(0, 200),
          duration_ms: Date.now() - t0,
          error: String(e?.message || e).slice(0, 200),
        });
        throw e;
      }
    };
  }

  // ─── G. Render check après 2s + 5s ─────────────────────────────
  setTimeout(() => {
    try {
      const root = document.querySelector("#root");
      const fallback = document.querySelector(".gively-boot-fallback");
      _log("RENDER", "check_2s", {
        root_present: Boolean(root),
        root_offset_height: root?.offsetHeight ?? null,
        root_children_count: root?.children?.length ?? 0,
        fallback_still_visible: Boolean(fallback),
        body_inner_html_len: document.body?.innerHTML?.length ?? 0,
      });
    } catch (e) { /* ignore */ }
  }, 2000);

  setTimeout(() => {
    try {
      const root = document.querySelector("#root");
      const fallback = document.querySelector(".gively-boot-fallback");
      _log("RENDER", "check_5s", {
        root_present: Boolean(root),
        root_offset_height: root?.offsetHeight ?? null,
        root_children_count: root?.children?.length ?? 0,
        fallback_still_visible: Boolean(fallback),
      });
      if (fallback) {
        _err("RENDER", "fallback_still_visible_5s_react_did_not_mount");
      }
    } catch (e) { /* ignore */ }
  }, 5000);

  // ─── H. Interaction (1er click confirme JS vivant) ──────────────
  let _firstClickLogged = false;
  if (typeof document !== "undefined") {
    document.addEventListener("click", () => {
      if (_firstClickLogged) return;
      _firstClickLogged = true;
      _log("INTERACTION", "first_click_detected");
    }, { passive: true });
  }

  // ─── I. Heartbeat toutes les 10s (réduit pour pas spammer) ─────
  let _heartbeatCount = 0;
  setInterval(() => {
    _heartbeatCount++;
    // Heartbeat envoyé uniquement les 6 premiers (= 1 minute) pour
    // pas inonder les logs Railway sur sessions longues.
    if (_heartbeatCount <= 6) {
      _log("HEARTBEAT", `tick_${_heartbeatCount}`, {
        elapsed_s: _heartbeatCount * 10,
      });
    }
  }, 10000);
}

// Auto-install au moment de l'import — utile si quelqu'un l'oublie.
installIOSDebug();
